/** @format */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useUser } from "@/api/userAPI";
import { useNotificationsEnabled } from "@/hooks/useStableFeatureFlags";

import galaxieClient, { isHttpError } from "./galaxieClient";

/**
 * True when the server told us this account has no notifications feature.
 *
 * The client gates on PostHog's flag list (`useNotificationsEnabled`) while the
 * backend re-evaluates `notifications` server-side for `request.user`. Identify
 * timing, the anonymous-vs-user distinct_id and the 60s server-side flag cache
 * all let those disagree, and the resulting 403 surfaced as
 * JAVASCRIPT-REACT-JA across 35 users. A disabled feature is an answer, not a
 * failure — only 403 is treated this way, so a genuine 5xx still reports.
 */
const isFeatureDisabled = (error: unknown): boolean =>
  isHttpError(error) && error.status === 403;

// Types
export interface Notification {
  id: string;
  category: string; // race_start, race_end, race_incident, favorite_driver_position, etc.
  title: string;
  body: string;
  data: Record<string, unknown>; // race_id, driver_id, url, top_5 positions
  status: "pending" | "sent" | "failed" | "skipped";
  deliveryMethod: "push" | "websocket";
  createdAt: string;
  sentAt: string | null;
  readAt: string | null;
}

interface NotificationHistoryPage {
  results: Notification[];
  nextCursor: string | null;
}

// Fetch notification history with cursor-based pagination
export const useNotificationHistory = (category?: string) => {
  const { data: user } = useUser();
  const notificationsEnabled = useNotificationsEnabled();
  return useInfiniteQuery<NotificationHistoryPage>({
    queryKey: ["notifications", "history", { category }],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", pageParam as string);
      if (category) params.set("category", category);
      try {
        const response = await galaxieClient.get(`/notifications/history/?${params}`, {
          headers: { "Cache-Control": "no-cache, no-store" },
        });
        return response.data as NotificationHistoryPage;
      } catch (error) {
        // Same client/server flag disagreement as the unread count.
        if (isFeatureDisabled(error)) {
          return { results: [], nextCursor: null } as NotificationHistoryPage;
        }
        throw error;
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    staleTime: 30000,
    refetchInterval: false, // WebSocket will update
    enabled: !!user?.id && notificationsEnabled,
  });
};

// Get unread notification count
export const useUnreadNotificationCount = () => {
  const { data: user } = useUser();
  const notificationsEnabled = useNotificationsEnabled();
  return useQuery<number>({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      try {
        const response = await galaxieClient.get("/notifications/unread-count/");
        return (response.data as { count: number }).count;
      } catch (error) {
        if (isFeatureDisabled(error)) return 0;
        throw error;
      }
    },
    staleTime: 10000,
    enabled: !!user?.id && notificationsEnabled,
  });
};

// Mark all notifications as read
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await galaxieClient.post("/notifications/mark-read/");
      return response.data as unknown;
    },
    onSuccess: async () => {
      queryClient.setQueryData(["notifications", "unread-count"], 0);
      await queryClient.invalidateQueries({ queryKey: ["notifications", "history"] });
    },
  });
};
